const SSO_COOKIE = '_op_id';
const SSO_COOKIE_KEY = '_op_key';
const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN || '';

function setCookie(name: string, value: string, days: number, options?: { noDomain?: boolean }) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const domain = !options?.noDomain && COOKIE_DOMAIN ? `; domain=${COOKIE_DOMAIN}` : '';
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domain}${secure}; SameSite=Lax`;
}

function removeCookie(name: string) {
  const domain = COOKIE_DOMAIN ? `; domain=${COOKIE_DOMAIN}` : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[2]);
  } catch {
    // Malformed cookie value — treat as missing to prevent app crash
    return undefined;
  }
}

export function setSsoCookies(token: string, tokenId: string, expiresDate: Date) {
  const days = Math.max(1, (expiresDate.getTime() - Date.now()) / 864e5);
  setCookie(SSO_COOKIE, token, days);
  setCookie(SSO_COOKIE_KEY, tokenId, 365 * 10);
}

export function clearSsoCookies() {
  removeCookie(SSO_COOKIE);
  removeCookie(SSO_COOKIE_KEY);
}

export function getSsoCookieKey(): string | undefined {
  return getCookie(SSO_COOKIE_KEY);
}

// Dual-live routing cookie — tells CF Worker which app to serve
// See: docs/architecture/dual-live-scio1-scio3-strategy.md
const SCIO_VER_COOKIE = '__Host-scio_ver';

/** True when running behind CF Worker — must match route hostnames in infra/cf-worker/wrangler.toml */
export function isBehindCfWorker(): boolean {
  const h = window.location.hostname;
  return /^app[-.]/.test(h) || /^(applive|qa3|beta3|staging3)\.optisigns\.com$/.test(h);
}

export function getScioVersionCookie(): string | undefined {
  return getCookie(SCIO_VER_COOKIE);
}

export function clearScioVersionCookie() {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SCIO_VER_COOKIE}=; path=/; max-age=0${secure}; SameSite=Lax`;
}

/**
 * Dual-live version routing decision.
 * Must match scio1 getEffectiveVersion() in imports/services/scio-version.js
 * Priority: userScioVersion (if set) > companyScioVersion > default=1
 */
export function getEffectiveVersion(
  companyScioVersion: number | undefined | null,
  userScioVersion: number | undefined | null,
): 1 | 3 {
  if (userScioVersion != null) {
    if (userScioVersion === 1) return 1;
    if (userScioVersion >= 2) return 3;
  }
  if (companyScioVersion != null) {
    if (companyScioVersion >= 2) return 3;
  }
  return 1;
}

/**
 * Full dual-live routing: compute effective version, set cookie, redirect if needed.
 * Extracted from AuthProvider to keep routing logic isolated and testable.
 *
 * @returns true if a redirect was initiated (caller should return early)
 */
export function handleDualLiveRouting(
  companyScioVersion: number | undefined | null,
  userScioVersion: number | undefined | null,
): boolean {
  if (!isBehindCfWorker()) return false;

  const effectiveVersion = getEffectiveVersion(companyScioVersion, userScioVersion);
  const currentCookie = getScioVersionCookie();
  const targetVersion: '1' | '3' = effectiveVersion === 3 ? '3' : '1';

  if (currentCookie !== targetVersion) {
    // Cookie mismatches user's effective version (e.g. different user logged in).
    // Use localStorage guard (survives cross-origin CF Worker hops scio1↔scio3).
    const RELOAD_KEY = 'scio_ver_redirect';
    const lastRedirect = localStorage.getItem(RELOAD_KEY);
    const now = Date.now();

    if (lastRedirect && now - Number(lastRedirect) < 10_000) {
      // Already redirected within 10s — abort to prevent loop
      localStorage.removeItem(RELOAD_KEY);
      console.error('Scio version redirect loop detected — aborting');
      return false;
    }

    localStorage.setItem(RELOAD_KEY, String(now));
    setScioVersionCookie(targetVersion);
    if (targetVersion === '1') {
      // Routing to scio1 — pass ?scio3_auth=1 so scio1 forces Firebase re-login
      window.location.href = '/signIn?scio3_auth=1';
    } else {
      // Routing to scio3 — cookie=3 already set, reload to get CF Worker to serve scio3
      window.location.reload();
    }
    return true;
  }

  if (effectiveVersion === 3) {
    // Cookie already correct, ensure it's set (might be first login)
    setScioVersionCookie('3');
  }
  return false;
}

export function setScioVersionCookie(version: '1' | '3') {
  setCookie(SCIO_VER_COOKIE, version, 365, { noDomain: true });
  // Clean up legacy cookie (old name before __Host- prefix migration)
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `__scio_ver=; path=/; max-age=0${secure}; SameSite=Lax`; // remove stale legacy cookie
}
